/**
 * Idempotency keys (TR-017). The server replays the stored result when a request arrives again with the same
 * `Idem-Key`, so a retry must reuse the key of the attempt it repeats, and a new action must get a new one.
 */

/** 53-bit string hash (cyrb53): header values must be ASCII, and transcripts are not. */
function hash(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/**
 * The key for advancing a run: the same run, step version and answer always give the same key, because
 * the step version moves on once the server has applied a turn, so repeating all three is a retry.
 */
export function advanceKey(
  runId: string,
  stepVersion: number,
  body: unknown
): string {
  return `advance-${runId}-${stepVersion}-${hash(JSON.stringify(body))}`;
}

/**
 * Keys for actions that have no natural identity (starting a run). `keyFor(name)` returns the key of the
 * attempt in progress, so retrying after a failure reuses it; `done(name)` ends the action so the next start
 * is a new one.
 */
export function createPendingKeys(): {
  keyFor: (name: string) => string;
  done: (name: string) => void;
} {
  const pending = new Map<string, string>();
  return {
    keyFor: (name: string): string => {
      let key = pending.get(name);
      if (key === undefined) {
        key = crypto.randomUUID();
        pending.set(name, key);
      }
      return key;
    },
    done: (name: string): void => {
      pending.delete(name);
    },
  };
}
